import { useState } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dropzone } from "@/components/ui/dropzone";
import { apiErrorMessage } from "@/lib/apiError";
import {
  submitFeedback,
  type FeedbackKind,
  type FeedbackSeverity,
  type SubmittedFeedback,
} from "@/lib/feedback";

const KINDS: { value: FeedbackKind; label: string; hint: string }[] = [
  {
    value: "bug",
    label: "Something is broken",
    hint: "It does not work, or it does the wrong thing.",
  },
  {
    value: "feature",
    label: "I need something",
    hint: "A field, a screen or a step that is missing.",
  },
  {
    value: "other",
    label: "Something else",
    hint: "A question, a confusing wording, anything worth saying.",
  },
];

const DESCRIPTION_LABELS: Record<FeedbackKind, string> = {
  bug: "What happened",
  feature: "What would you like to be able to do",
  other: "Details",
};

const DESCRIPTION_PLACEHOLDERS: Record<FeedbackKind, string> = {
  bug: "Include the serial number or record you were working on",
  feature: "Describe it the way you would explain it to a colleague",
  other: "",
};

const IMAGE_LABELS: Record<FeedbackKind, string> = {
  bug: "Screenshots (optional)",
  feature: "Sketches or screenshots (optional)",
  other: "Screenshots (optional)",
};

const IMAGE_HINTS: Record<FeedbackKind, string> = {
  bug: "A screenshot of the error says more than a paragraph",
  feature: "Mark up a screenshot to show where it belongs",
  other: "Anything that helps explain it",
};

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "heic",
  "heif",
];
const IMAGE_ACCEPT = IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(",");

function hasAllowedExtension(file: File): boolean {
  const parts = file.name.toLowerCase().split(".");
  return parts.length > 1 && IMAGE_EXTENSIONS.includes(parts[parts.length - 1]);
}

const SEVERITIES: { value: FeedbackSeverity; label: string }[] = [
  { value: "low", label: "Low — cosmetic or minor" },
  { value: "medium", label: "Medium — annoying but workable" },
  { value: "high", label: "High — a task cannot be completed" },
  { value: "blocker", label: "Blocker — the console is unusable" },
];

const monoNote: React.CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 11,
  color: "var(--ink-mute)",
  lineHeight: 1.6,
};

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [benefit, setBenefit] = useState("");
  const [severity, setSeverity] = useState<FeedbackSeverity>("medium");
  const [images, setImages] = useState<File[]>([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<SubmittedFeedback | null>(null);

  function reset() {
    setKind("bug");
    setTitle("");
    setDescription("");
    setSteps("");
    setExpected("");
    setBenefit("");
    setSeverity("medium");
    setImages([]);
    setError("");
    setCreated(null);
  }

  function close() {
    reset();
    onClose();
  }

  function acceptImages(next: File[]) {
    const wrongType = next.find((file) => !hasAllowedExtension(file));
    const byType = next.filter(hasAllowedExtension);
    const tooBig = byType.find((file) => file.size > MAX_IMAGE_BYTES);
    const kept = byType.filter((file) => file.size <= MAX_IMAGE_BYTES);

    if (wrongType) {
      setError(
        `${wrongType.name} was not attached — use JPG, PNG, GIF, WEBP, BMP or HEIC`
      );
    } else if (tooBig) {
      setError(`${tooBig.name} is larger than 10 MB and was not attached`);
    } else if (kept.length > MAX_IMAGES) {
      setError(`Only the first ${MAX_IMAGES} images are attached`);
    } else {
      setError("");
    }

    setImages(kept.slice(0, MAX_IMAGES));
  }

  const isBug = kind === "bug";
  const isFeature = kind === "feature";
  const activeKind = KINDS.find((k) => k.value === kind);

  async function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const issue = await submitFeedback(
        {
          kind,
          title: title.trim(),
          description: description.trim(),
          steps: isBug ? steps.trim() || undefined : undefined,
          expected: isBug ? expected.trim() || undefined : undefined,
          benefit: isFeature ? benefit.trim() || undefined : undefined,
          severity: isBug ? severity : undefined,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          appVersion: (import.meta.env.VITE_APP_VERSION as string) || undefined,
        },
        images
      );
      setCreated(issue);
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Could not send this"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} title="Send feedback" onClose={close}>
      {created ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div
            style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink-soft)" }}
          >
            Thank you — this was filed as{" "}
            <b style={{ color: "var(--ink)" }}>#{created.number}</b> and the
            team can see it now.
          </div>
          <a
            href={created.url}
            target="_blank"
            rel="noreferrer"
            style={{ ...monoNote, color: "var(--accent)" }}
          >
            Open it ↗
          </a>
          <div style={{ display: "flex", gap: 12 }}>
            <Button size="sm" onClick={reset}>
              Send another
            </Button>
            <Button size="sm" variant="ghost" onClick={close}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form className="form" onSubmit={handleSubmit}>
          <div className="field">
            <Label>What is this about</Label>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {KINDS.map((k) => (
                <button
                  key={k.value}
                  type="button"
                  onClick={() => setKind(k.value)}
                  disabled={submitting}
                  style={{
                    flex: "1 1 0",
                    padding: "10px 12px",
                    background:
                      kind === k.value ? "var(--surface-3)" : "transparent",
                    border: `1px solid ${
                      kind === k.value ? "var(--accent)" : "var(--line)"
                    }`,
                    color: kind === k.value ? "var(--ink)" : "var(--ink-mute)",
                    fontFamily: "var(--f-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    cursor: submitting ? "default" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {k.label}
                </button>
              ))}
            </div>
            {activeKind && <div style={monoNote}>{activeKind.hint}</div>}
          </div>

          <div className="field">
            <Label htmlFor="feedback-title">Summary</Label>
            <Input
              id="feedback-title"
              required
              minLength={4}
              maxLength={160}
              placeholder="One line"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>

          <div className="field">
            <Label htmlFor="feedback-description">
              {DESCRIPTION_LABELS[kind]}
            </Label>
            <textarea
              id="feedback-description"
              className="textarea"
              required
              minLength={10}
              maxLength={5000}
              rows={4}
              placeholder={DESCRIPTION_PLACEHOLDERS[kind]}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
            />
          </div>

          {isBug && (
            <>
              <div className="field">
                <Label htmlFor="feedback-steps">
                  Steps to reproduce (optional)
                </Label>
                <textarea
                  id="feedback-steps"
                  className="textarea"
                  maxLength={2000}
                  rows={3}
                  placeholder="1. Open Passports…"
                  value={steps}
                  onChange={(e) => setSteps(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="field">
                <Label htmlFor="feedback-expected">
                  What you expected (optional)
                </Label>
                <textarea
                  id="feedback-expected"
                  className="textarea"
                  maxLength={2000}
                  rows={2}
                  value={expected}
                  onChange={(e) => setExpected(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="field">
                <Label htmlFor="feedback-severity">How bad is it</Label>
                <select
                  id="feedback-severity"
                  className="select"
                  value={severity}
                  onChange={(e) =>
                    setSeverity(e.target.value as FeedbackSeverity)
                  }
                  disabled={submitting}
                  style={{
                    padding: "10px 14px",
                    fontSize: 13,
                    appearance: "none",
                  }}
                >
                  {SEVERITIES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {isFeature && (
            <div className="field">
              <Label htmlFor="feedback-benefit">
                Why it would help (optional)
              </Label>
              <textarea
                id="feedback-benefit"
                className="textarea"
                maxLength={2000}
                rows={2}
                placeholder="What it saves you, or what you do instead today"
                value={benefit}
                onChange={(e) => setBenefit(e.target.value)}
                disabled={submitting}
              />
            </div>
          )}

          <div className="field">
            <Label>{IMAGE_LABELS[kind]}</Label>
            <Dropzone
              accept={IMAGE_ACCEPT}
              disabled={submitting}
              files={images}
              onFiles={acceptImages}
              hint={`${IMAGE_HINTS[kind]} · up to ${MAX_IMAGES}, 10 MB each`}
            />
          </div>

          <div style={monoNote}>
            Your email, the page you are on and your browser are attached
            automatically.
          </div>

          {error && (
            <div style={{ ...monoNote, color: "var(--danger)" }}>{error}</div>
          )}

          <div style={{ display: "flex", gap: 12 }}>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? "Sending…" : "Send"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={close}
              disabled={submitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
